import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { revealEnvValue } from "@/lib/api";
import { RevealCache } from "@/lib/envMatrix";

/**
 * Secret values, fetched one at a time from the server and never shipped with
 * the page. Three ways in, all admin-only and audited:
 * - hover a secret in the table for a moment (useHoverReveal);
 * - open a key's details, which shows its values side by side (useRevealNow);
 * - click a secret to copy it (useSecretForCopy).
 * The pause before a hover reveal keeps a pointer sweeping across the grid
 * from revealing, and auditing, every value it crosses.
 */
const REVEAL_DELAY_MS = 350;

interface RevealCtx {
  cache: RevealCache;
  canReveal: boolean;
}

const RevealContext = createContext<RevealCtx | null>(null);

/** One cache per page view; leaving the page forgets every revealed value. */
export function RevealProvider({ canReveal, children }: { canReveal: boolean; children: React.ReactNode }) {
  const cache = useMemo(
    () => new RevealCache((serviceId, key) => revealEnvValue(serviceId, key).then((r) => r.value)),
    [],
  );
  useEffect(() => () => cache.clear(), [cache]);
  return <RevealContext.Provider value={{ cache, canReveal }}>{children}</RevealContext.Provider>;
}

export interface RevealTarget {
  serviceId: string;
  key: string;
  fingerprint: string | null;
  revealable: boolean;
}

export type RevealState =
  | { status: "hidden" }
  | { status: "loading" }
  | { status: "shown"; value: string }
  | { status: "unavailable"; reason: string };

/** Why this viewer can't have this value, or null if they can. */
function blocked(ctx: RevealCtx | null, target: RevealTarget, verb: "see" | "copy"): string | null {
  if (!ctx || !ctx.canReveal) return `Only admins can ${verb} secret values.`;
  if (!target.revealable) {
    return verb === "see"
      ? "The portal never shows its own secrets."
      : "The portal never hands out its own secrets.";
  }
  return null;
}

export function useHoverReveal(target: RevealTarget | null) {
  const ctx = useContext(RevealContext);
  const [state, setState] = useState<RevealState>({ status: "hidden" });
  const timer = useRef<number | null>(null);
  const live = useRef(false);

  const hide = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    live.current = false;
    setState({ status: "hidden" });
  };

  const start = () => {
    if (!target || live.current) return;
    live.current = true;
    timer.current = window.setTimeout(() => {
      const reason = blocked(ctx, target, "see");
      if (reason) {
        setState({ status: "unavailable", reason });
        return;
      }
      setState({ status: "loading" });
      ctx!.cache.get(target.serviceId, target.key, target.fingerprint).then(
        (value) => live.current && setState({ status: "shown", value }),
        (e: Error) =>
          live.current && setState({ status: "unavailable", reason: `Couldn't reveal it: ${e.message}` }),
      );
    }, REVEAL_DELAY_MS);
  };

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  return {
    state,
    bind: { onMouseEnter: start, onMouseLeave: hide, onFocus: start, onBlur: hide },
  };
}

/** Reveals a secret as soon as it's on screen — for the key details modal, opened on purpose. */
export function useRevealNow(target: RevealTarget | null): RevealState {
  const ctx = useContext(RevealContext);
  const [state, setState] = useState<RevealState>({ status: "hidden" });
  const id = target ? `${target.serviceId}|${target.key}|${target.fingerprint}|${target.revealable}` : null;

  useEffect(() => {
    if (!target) return;
    const reason = blocked(ctx, target, "see");
    if (reason) {
      setState({ status: "unavailable", reason });
      return;
    }
    let live = true;
    setState({ status: "loading" });
    ctx!.cache.get(target.serviceId, target.key, target.fingerprint).then(
      (value) => live && setState({ status: "shown", value }),
      (e: Error) => live && setState({ status: "unavailable", reason: `Couldn't reveal it: ${e.message}` }),
    );
    return () => {
      live = false;
    };
    // `id` captures everything about the target that matters.
  }, [id, ctx]);

  return state;
}

/**
 * For copying a secret: its value as a promise (the click's clipboard
 * permission waits on it), or the reason this viewer can't have it.
 */
export function useSecretForCopy() {
  const ctx = useContext(RevealContext);
  return (target: RevealTarget): { ok: true; value: Promise<string> } | { ok: false; reason: string } => {
    const reason = blocked(ctx, target, "copy");
    if (reason) return { ok: false, reason };
    return { ok: true, value: ctx!.cache.get(target.serviceId, target.key, target.fingerprint) };
  };
}
